<template>
  <div ref="raiz" class="cb" :class="{ abierto }">
    <input
      :id="id"
      ref="input"
      class="app-field-control cb-input"
      type="text"
      role="combobox"
      autocomplete="off"
      aria-autocomplete="list"
      :value="abierto ? consulta : etiquetaSeleccionada"
      :placeholder="abierto && etiquetaSeleccionada ? etiquetaSeleccionada : placeholder"
      :aria-expanded="abierto ? 'true' : 'false'"
      :aria-controls="listaId"
      :aria-activedescendant="abierto && filtradas[activa] ? opcionId(activa) : null"
      :aria-invalid="invalid ? 'true' : null"
      :aria-describedby="describedby"
      :data-testid="testid"
      @focus="abrir"
      @click="abrir"
      @input="alEscribir"
      @keydown="alTeclear"
      @blur="alSalir"
    />
    <AppIcon name="chevron-down" size="h-4 w-4" class="cb-chevron" :class="{ girado: abierto }" />

    <ul
      v-if="abierto"
      :id="listaId"
      ref="lista"
      role="listbox"
      class="cb-lista"
      :class="{ arriba: abrirArriba }"
      :style="{ maxHeight: altoMaximo + 'px' }"
      :data-testid="testid + '-lista'"
    >
      <li
        v-for="(opcion, i) in filtradas"
        :id="opcionId(i)"
        :key="opcion.value"
        role="option"
        class="cb-opcion"
        :class="{ activa: i === activa, elegida: opcion.value === value }"
        :aria-selected="opcion.value === value ? 'true' : 'false'"
        :data-testid="testid + '-opcion-' + opcion.value"
        @mousedown.prevent="elegir(opcion)"
        @mousemove="activa = i"
      >
        <!-- Slot `opcion` para dibujar algo más que el texto (p. ej. el logo de la marca) -->
        <slot name="opcion" :opcion="opcion">
          <span v-if="opcion.codigo" class="cb-codigo">{{ opcion.codigo }}</span>
          <span class="cb-texto">{{ opcion.label }}</span>
        </slot>
        <AppIcon v-if="opcion.value === value" name="check" size="h-4 w-4" class="cb-check" />
      </li>
      <li v-if="!filtradas.length" class="cb-vacio" role="presentation">
        Sin coincidencias para “{{ consulta }}”
      </li>
    </ul>
  </div>
</template>

<script>
/*
 * AppCombobox — select con buscador (patrón combobox editable de ARIA 1.2).
 *
 * Reemplaza al <select> nativo cuando hay muchas opciones (régimen fiscal,
 * marcas, modelos). Filtra por clave o texto sin importar tildes ni
 * mayúsculas; se maneja con flechas, Enter y Esc; si abajo no cabe la lista
 * (p. ej. dentro de un modal), abre hacia arriba para no generar scroll.
 *
 * Uso: <AppCombobox v-model="form.regimen" :options="[{ value, label, codigo }]" />
 * Con <template #opcion="{ opcion }"> cada opción puede llevar más que texto.
 */
import AppIcon from '@/components/ui/AppIcon.vue'
import { posicionLista } from '@/utils/posicion'

const ALTO_LISTA = 264
let consecutivo = 0

function normalizar(texto) {
  return String(texto ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim()
}

export default {
  name: 'AppCombobox',
  components: { AppIcon },
  props: {
    value: { type: [String, Number], default: '' },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Buscar…' },
    id: { type: String, default: null },
    invalid: { type: Boolean, default: false },
    describedby: { type: String, default: null },
    testid: { type: String, default: 'combobox' },
  },
  data() {
    return {
      abierto: false,
      consulta: '',
      activa: 0,
      abrirArriba: false,
      altoMaximo: ALTO_LISTA,
      listaId: `cb-lista-${++consecutivo}`,
    }
  },
  computed: {
    seleccionada() {
      return this.options.find((o) => o.value === this.value) || null
    },
    etiquetaSeleccionada() {
      const o = this.seleccionada
      if (!o) return ''
      return o.codigo ? `${o.codigo} — ${o.label}` : o.label
    },
    filtradas() {
      const q = normalizar(this.consulta)
      if (!q) return this.options
      return this.options.filter((o) => normalizar(`${o.codigo || ''} ${o.label}`).includes(q))
    },
  },
  watch: {
    activa() {
      this.$nextTick(() => {
        const el = this.$refs.lista && this.$refs.lista.querySelector('.cb-opcion.activa')
        if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' })
      })
    },
  },
  methods: {
    opcionId(i) {
      return `${this.listaId}-${i}`
    },
    abrir() {
      if (this.abierto) return
      this.consulta = ''
      const indice = this.options.findIndex((o) => o.value === this.value)
      this.activa = indice >= 0 ? indice : 0
      const { arriba, altoMaximo } = posicionLista(this.$refs.raiz, ALTO_LISTA)
      this.abrirArriba = arriba
      this.altoMaximo = altoMaximo
      this.abierto = true
    },
    cerrar() {
      this.abierto = false
      this.consulta = ''
    },
    alEscribir(event) {
      this.consulta = event.target.value
      if (!this.abierto) this.abrir()
      this.consulta = event.target.value
      this.activa = 0
    },
    alTeclear(event) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        if (!this.abierto) return this.abrir()
        this.activa = Math.min(this.activa + 1, this.filtradas.length - 1)
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        this.activa = Math.max(this.activa - 1, 0)
      } else if (event.key === 'Enter') {
        if (!this.abierto) return
        event.preventDefault()
        const opcion = this.filtradas[this.activa]
        if (opcion) this.elegir(opcion)
      } else if (event.key === 'Escape') {
        if (!this.abierto) return
        // Esc cierra la lista, no el modal que la contiene
        event.stopPropagation()
        this.cerrar()
      } else if (event.key === 'Tab') {
        this.cerrar()
      }
    },
    elegir(opcion) {
      this.$emit('input', opcion.value)
      this.cerrar()
    },
    alSalir() {
      this.cerrar()
      this.$emit('blur')
    },
  },
}
</script>

<style scoped>
.cb {
  position: relative;
}
.cb .cb-input {
  padding-right: 36px;
  text-overflow: ellipsis;
  cursor: pointer;
}
.cb.abierto .cb-input {
  cursor: text;
}
.cb-chevron {
  position: absolute;
  top: 50%;
  right: 12px;
  color: #607187;
  pointer-events: none;
  transform: translateY(-50%);
  transition: transform 0.16s ease;
}
.cb-chevron.girado {
  transform: translateY(-50%) rotate(180deg);
}
.cb-lista {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  left: 0;
  z-index: 50;
  max-height: 264px;
  margin: 0;
  padding: 6px;
  overflow-y: auto;
  border: 1px solid #e4e9ef;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 12px 32px rgba(16, 42, 67, 0.16);
  list-style: none;
}
.cb-lista.arriba {
  top: auto;
  bottom: calc(100% + 6px);
}
.cb-opcion {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  color: #0b1b2e;
  font-size: 13px;
  line-height: 1.35;
  cursor: pointer;
}
.cb-opcion.activa {
  background: #edf3f8;
}
.cb-opcion.elegida {
  font-weight: 600;
}
.cb-codigo {
  flex: none;
  min-width: 30px;
  color: #0a4f9c;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.cb-texto {
  flex: 1;
  min-width: 0;
}
.cb-check {
  flex: none;
  color: #0a4f9c;
}
.cb-vacio {
  padding: 10px;
  color: #607187;
  font-size: 13px;
}
@media (prefers-reduced-motion: reduce) {
  .cb-chevron {
    transition: none;
  }
}
</style>
